import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconChevronRight,
  IconLeaf,
  IconPillFilled,
  IconPrescription,
} from "@tabler/icons-react";
import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { getMemberInfo } from "@/hooks/useMembers";
import { documentService } from "@/services/documents.service";
import { usePillbox } from "@/hooks/usePillbox";
import {
  MedicineItemType,
  PendingChangeType,
  PillboxPendingChange,
} from "@/types/pillbox";
import {
  formatDoctorDateBadge,
  formatFrequencyLine,
  formatMedicineTitle,
  formatShortDate,
} from "./utils/formatMedicine";
import {
  groupPendingChanges,
  mapPendingChangeToDraftItem,
  PendingGroup,
} from "./utils/pendingChange";
import styles from "./ReviewMedications.module.css";

const ReviewMedications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  // `onBehalf` is false when the viewer is looking at their own Pillbox, so
  // the copy addresses them directly ("your medications") instead of using
  // their own name back at them.
  const isSelf = !memberInfo.onBehalf;
  const { fetchPending, rejectPending } = usePillbox(memberId);

  const [pending, setPending] = useState<PillboxPendingChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(
    null
  );

  const loadPending = useCallback(() => {
    if (!memberId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPending()
      .then(setPending)
      .finally(() => setLoading(false));
  }, [memberId, fetchPending]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const addOnly = useMemo(
    () => pending.filter((p) => p.changeType === PendingChangeType.ADD),
    [pending]
  );
  const groups = useMemo(() => groupPendingChanges(pending), [pending]);
  const prescriptionCount = useMemo(
    () => new Set(addOnly.map((p) => p.sourcePrescriptionId)).size,
    [addOnly]
  );

  const handleDontAdd = async (group: PendingGroup) => {
    await Promise.allSettled(group.items.map((i) => rejectPending(i.id)));
    loadPending();
  };

  const handleReview = (group: PendingGroup) => {
    if (group.items.length > 1) {
      navigate("/pillbox/review/choose", { state: { group } });
      return;
    }
    const item = group.items[0];
    navigate("/pillbox/add-medicine", {
      state: {
        mode: "review",
        item: mapPendingChangeToDraftItem(item),
        pendingId: item.id,
      },
    });
  };

  const handleOpenPrescriptions = () => {
    navigate("/pillbox/review/prescriptions", { state: { pending: addOnly } });
  };

  // Tapping a medicine name opens the prescription it was extracted from.
  // Only offered for ungrouped cards — a grouped card spans several
  // prescriptions, so there's no single document the name maps to.
  const handleOpenSourceDocument = async (documentId: string) => {
    if (openingDocumentId) return;
    setOpeningDocumentId(documentId);
    try {
      const document = await documentService(memberId).getById(documentId);
      navigate("/documents/view", {
        state: { document, backUrl: window.location.pathname },
      });
    } finally {
      setOpeningDocumentId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <MLoader />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate("/pillbox")}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
        </div>
        <div className={styles.textHeader}>
          <h1 className={styles.title}>
            {isSelf
              ? t("pillbox.review.titleSelf")
              : t("pillbox.review.title", { member: memberDisplayName })}
          </h1>
          <p className={styles.subtitle}>
            {isSelf
              ? t("pillbox.review.subtitleSelf")
              : t("pillbox.review.subtitle", { member: memberDisplayName })}
          </p>

          {groups.length > 0 && (
            <button
              type="button"
              className={styles.summaryCard}
              onClick={handleOpenPrescriptions}
            >
              <span className={styles.summaryIcon}>
                <IconPrescription size={24} stroke={1.5} />
              </span>
              <span className={styles.summaryText}>
                <span className={styles.summaryCount}>
                  {t("pillbox.review.newMedications", {
                    count: addOnly.length,
                  })}
                </span>
                <span className={styles.summaryPrescriptions}>
                  {t("pillbox.review.fromPrescriptions", {
                    count: prescriptionCount,
                  })}
                </span>
              </span>
              <IconChevronRight size={24} className={styles.summaryChevron} />
            </button>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className={styles.emptyCardWrap}>
          <MEmptyCard
            icon={<IconPrescription size={64} stroke={1.5} color="#AD95D1" />}
            primaryTitle={t("pillbox.review.emptyTitle")}
          />
        </div>
      ) : (
        <div className={styles.list}>
          {groups.map((group) => {
            const isGrouped = group.items.length > 1;
            const single = group.items[0];
            const singleDraft = !isGrouped
              ? mapPendingChangeToDraftItem(single)
              : null;
            const isSupplement = group.itemType === MedicineItemType.SUPPLEMENT;
            return (
              <div className={styles.card} key={group.key}>
                <div className={styles.cardMain}>
                  <span className={styles.cardIcon}>
                    {isSupplement ? (
                      <IconLeaf size={24} stroke={1.5} />
                    ) : (
                      <IconPillFilled size={24} />
                    )}
                  </span>
                  <div className={styles.cardContent}>
                    {isGrouped ? (
                      <span className={styles.groupBadge}>
                        {t("pillbox.review.inPrescriptions", {
                          count: group.items.length,
                        })}
                      </span>
                    ) : (
                      <span className={styles.doctorDate}>
                        {formatDoctorDateBadge(
                          single.doctorName,
                          single.dateOfVisit
                            ? formatShortDate(single.dateOfVisit)
                            : undefined,
                          t("pillbox.review.unknownDoctor")
                        )}
                      </span>
                    )}
                    {singleDraft && single.sourceDocumentId ? (
                      <button
                        type="button"
                        className={styles.nameButton}
                        disabled={openingDocumentId === single.sourceDocumentId}
                        onClick={() =>
                          handleOpenSourceDocument(single.sourceDocumentId)
                        }
                      >
                        {formatMedicineTitle(singleDraft)}
                      </button>
                    ) : (
                      <p className={styles.name}>
                        {singleDraft
                          ? formatMedicineTitle(singleDraft)
                          : group.name}
                      </p>
                    )}
                    {singleDraft && (
                      <p className={styles.frequency}>
                        {formatFrequencyLine(
                          singleDraft,
                          singleDraft.recurrenceIntervalDays
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className={styles.divider} />
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.dontAddButton}
                    onClick={() => handleDontAdd(group)}
                  >
                    {t("pillbox.review.dontAdd")}
                  </button>
                  <button
                    type="button"
                    className={styles.reviewButton}
                    onClick={() => handleReview(group)}
                  >
                    {t("pillbox.review.review")}
                    <IconChevronRight size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewMedications;
